import {
  AccessRefusalKindEnum,
  ExecutionStatusEnum,
} from '@e-socna/data-layer';

import type { AccessGate } from '../types/access-gate.type';

interface Answer {
  readonly status: ExecutionStatusEnum;
  readonly details: readonly Readonly<{
    kind: AccessRefusalKindEnum;
    fr: string;
  }>[];
}

const KIND_LABELS: Readonly<Record<AccessRefusalKindEnum, string>> = {
  [AccessRefusalKindEnum.Rights]: 'Droits',
  [AccessRefusalKindEnum.State]: 'État',
};

// While the answer is loading, the action stays closed, without reason.
export const UNKNOWN_YET: AccessGate = { isAllowed: false, reasons: [] };

const reasonsOf = (answers: readonly Answer[]): readonly string[] => [
  ...new Set(
    answers.flatMap(({ details }) =>
      details.map(({ kind, fr }) => `${KIND_LABELS[kind]} : ${fr}`),
    ),
  ),
];

export const gateOf = (answer: Answer | undefined): AccessGate =>
  answer
    ? {
        isAllowed: answer.status === ExecutionStatusEnum.Ok,
        reasons: reasonsOf([answer]),
      }
    : UNKNOWN_YET;

// A state change is open when at least one action of the current state is.
export const stateChangeGateOf = (
  actions: readonly Answer[] | null | undefined,
): AccessGate => {
  if (!actions) {
    return UNKNOWN_YET;
  }

  const isAllowed = actions.some(
    ({ status }) => status === ExecutionStatusEnum.Ok,
  );

  return {
    isAllowed,
    reasons: isAllowed
      ? []
      : actions.length
        ? reasonsOf(actions)
        : ["État : aucune action possible dans l'état actuel."],
  };
};

export const OPEN: AccessGate = { isAllowed: true, reasons: [] };

// An action requiring several gates: open when all of them are, closed for all their reasons.
export const allGatesOf = (...gates: readonly AccessGate[]): AccessGate => ({
  isAllowed: gates.every(({ isAllowed }) => isAllowed),
  reasons: [...new Set(gates.flatMap(({ reasons }) => reasons))],
});
